import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  Inject,
  Optional,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '@app/app.service';
import { enumEntrySummarySubmissionType, enumTransportationMode } from '@app/data-layer/enums/entry-summaries';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import {
  CountryModel,
  CurrencyRateModel,
  HtsNumberModel,
  ShipmentModel,
  StateModel,
  UnitModel,
  ValidationMessageModel,
} from '@app/data-layer/models/common';
import { SimilarHtsSuggestionModel } from '@app/data-layer/models/common';
import { FavoriteHtsNumberModel } from '@app/data-layer/models/common/favorite-hts-number.model';
import { CompanyModel } from '@app/data-layer/models/companies';
import { DocumentModel } from '@app/data-layer/models/documents';
import { EntrySummaryModel, InvoiceModel, LineModel } from '@app/data-layer/models/entry-summaries';
import { BinaRepository, CodeRepository, CurrenciesRepository } from '@app/data-layer/repositories';
import {
  CANADA_COUNTRY_ID,
  LINE_ITEM_DESCRIPTION_MAX_LENGTH,
  MEXICO_COUNTRY_ID,
  USA_COUNTRY_ID,
  WEIGHT_UNIT_KG,
} from '@constants/constants';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { CUSTOM_MASK, HTS_MASK } from '@helpers/custom-validator';
import { DateHelper } from '@helpers/date.helper';
import { EntrySummaryActionsHelper } from '@helpers/entry-summary-actions.helper';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { enumHtsChatScope } from '@data-layer/enums/common';
import { HtsAssistantDrawerComponent } from '@shared/hts-assistant/components';
import { enumHtsAssistantMode } from '@shared/hts-assistant/enums';
import { HtsAssistantDescriptionGuard } from '@shared/hts-assistant/helpers/hts-assistant-description.guard';
import { HtsAssistantHelper } from '@shared/hts-assistant/helpers/hts-assistant.helper';
import { HtsLineStatusHelper } from '@shared/hts-assistant/helpers/hts-line-status.helper';
import { IHtsAssistantHtsFound } from '@shared/hts-assistant/interfaces';
import { HTS_CHAT_SCOPE, HtsAssistantLauncherService, HtsLineInsertService } from '@shared/hts-assistant/services';
import { HTS_LINE_PANEL_Z_INDEX, HTS_SUGGESTIONS_DEBOUNCE_MS } from '@shared/hts-assistant/const/hts-assistant.const';
import { IEsValidationData } from '@interfaces/es-validation-data';
import { ISectionFormField } from '@interfaces/section-form-field';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { CompaniesService } from '@services/companies.service';
import { BinaSidebarComponent } from '@shared/bina-mutual/components';
import { BinaCatalogSearchSidebarComponent } from '@shared/bina-shared/components';
import { IEsDocumentStateUpdate } from '@shared/es-mutual/interfaces';
import { EntrySummaryStateService, EsOcrQuickFixService } from '@shared/es-mutual/services';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, debounceTime, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-es-ocr-quick-fix-line-item-details',
  templateUrl: './es-ocr-quick-fix-line-item-details.component.html',
  styleUrls: ['./es-ocr-quick-fix-line-item-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsOcrQuickFixLineItemDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public lineItemFields: ISectionFormField[];

  @Input() public shipmentId: number;
  @Input() public invoiceId: number;

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();
  /** The backend wrote a catalog / similar-product code onto the saved line (#13411 / #13517, tagged companies). */
  @Output() public lineInserted: EventEmitter<LineModel> = new EventEmitter();
  /** The pre-save or the backend insert was refused (the alert is shown): hosts re-read the form's dirty state. */
  @Output() public lineInsertFailed: EventEmitter<void> = new EventEmitter();

  @Input() public itemId: number;
  @Input() public isShowHint: boolean;
  @Input() public validateImmediately: boolean;
  /** #12863 — permanent "New line item" step: saves via its own Add button and resets to a blank form afterwards. */
  @Input() public isAddMode: boolean = false;

  public company: CompanyModel;
  public entrySummary: EntrySummaryModel;
  public shipment: ShipmentModel;
  public invoice: InvoiceModel;
  public itemForm: UntypedFormGroup;
  public arrivalDate: string;
  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public UNITS_LINES_LENGTH: number = 3;
  public decimalInputMap: InputTypeMap = INPUT_TYPES_MAP.DECIMAL;
  public integerInputMap: InputTypeMap = INPUT_TYPES_MAP.INTEGER;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;
  public invoiceCurrencyCode: string;
  public invoiceCurrencyRate: number;
  public CUSTOM_MASK: any = CUSTOM_MASK;
  public addSpecialChapterHTSCode: boolean = false;
  /** `assistant` comes first: "Find HTS" without a description sets only that error, so its hint wins over the generic one. */
  public errors: { assistant: string; required: string; maxlength: string } = {
    assistant: HtsAssistantDescriptionGuard.HINT_KEY,
    required: 'FORM_ERROR.REQUIRED',
    maxlength: 'FORM_ERROR.MAX_LENGTH',
  };
  public HTS_MASK: string = HTS_MASK;
  public isCanEdit: boolean = false;
  public isCanRemoveDocuments: boolean = false;
  public currentItem: LineModel;
  public unitsToMachForSpecialHts: string[] = [];
  public attachedDocuments: DocumentModel[] = [];
  public isShowDocuments: boolean = false;
  public isCustomBroker: boolean = false;
  public validationMessages: ValidationMessageModel[] = [];
  public WEIGHT_UNIT_KG: string = WEIGHT_UNIT_KG;
  public isInit: boolean = false;
  public initCountryOfExport: CountryModel[] = [];
  public initStateOfOrigin: StateModel[] = [];
  public initCountryOfOrigin: CountryModel[] = [];
  public shouldNotMatchHTSConfig = this.createShouldNotMatchHTSConfig();
  public uploadDocumentsDescsription: string;
  public uploadDocumentsDescsriptionMobile: string;
  public lastSearchedCountries: CountryModel[] = [];
  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public favoriteHtsNumberSearchedItems: FavoriteHtsNumberModel[] = [];
  /** Similar products the company classified before, for the typed description (#13517, tagged companies). */
  public favoriteHtsSuggestions: SimilarHtsSuggestionModel[] = [];
  public companyFavoriteHtsNumbers: FavoriteHtsNumberModel[] = [];

  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  private currentShipment: ShipmentModel;
  private currentInvoice: InvoiceModel;
  private isDestroy: ReplaySubject<boolean> = new ReplaySubject();
  /** #13383 F6: add mode — the line "Find HTS" already persisted; Add then PUTs it, the reset forgets it. */
  private createdLineId: number | null = null;
  private isPreSavingForAssistant: boolean = false;
  /** Next on a line the assistant has not verified (#13403): the assistant opens first, the submit resumes when it closes. */
  private isSubmitPendingAfterAssistant: boolean = false;
  private isDestroyed: boolean = false;
  /** `lastModified` of the store line when the drawer opened: the form is re-read only if the line changed meanwhile. */
  private lineModifiedAtDrawerOpen: string | null = null;
  private favoriteHtsItemsSearchSubject = new Subject<string>();
  private previousCountryOfOriginId: number | null = null;

  constructor(
    private entrySummaryStateService: EntrySummaryStateService,
    private activeRoute: ActivatedRoute,
    private fh: FormHelper,
    private elementRef: ElementRef<HTMLElement>,
    private location: Location,
    private binaRepository: BinaRepository,
    private esOcrQuickFixService: EsOcrQuickFixService,
    private currenciesRepository: CurrenciesRepository,
    private codeRepository: CodeRepository,
    private companiesService: CompaniesService,
    private translateService: TranslateService,
    private appService: AppService,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef,
    private htsAssistantLauncherService: HtsAssistantLauncherService,
    private htsLineInsertService: HtsLineInsertService,
    @Optional() @Inject(HTS_CHAT_SCOPE) private htsChatScope: enumHtsChatScope,
  ) {}

  public ngOnInit(): void {
    this.company = this.companiesService.getCompany();
    this.entrySummary = this.esOcrQuickFixService.getEntrySummary();
    this.shipment = this.entrySummary?.shipments.find((shipment) => shipment.id === this.shipmentId);
    this.invoice = this.shipment.invoices.find((invoice) => invoice.id === this.invoiceId);

    this.itemForm = this.fh.createLineItemForm();
    if (this.validateImmediately) {
      this.fh.validateAllFields(this.itemForm);
    }

    this.itemForm
      .get('countryOfOriginId')
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe((countryId: number) => {
        if (!this.addSpecialChapterHTSCode) {
          this.toggleSpecialHts();
        }

        const selectedCountry: CountryModel =
          this.lastSearchedCountries.find((c: CountryModel) => c.id === countryId) ||
          this.initCountryOfOrigin.find((c: CountryModel) => c.id === countryId);
        if (selectedCountry) {
          this.itemForm.get('countryOfOrigin').setValue(selectedCountry, { emitEvent: false });
        }

        const countryChanged: boolean = this.previousCountryOfOriginId !== null && this.previousCountryOfOriginId !== countryId;
        const requiresState: boolean = countryId === CANADA_COUNTRY_ID || countryId === MEXICO_COUNTRY_ID || countryId === USA_COUNTRY_ID;

        if (countryChanged) {
          this.itemForm.get('stateOfOrigin').reset();
        }
        if (requiresState) {
          this.itemForm.get('stateOfOrigin').setValidators(Validators.required);
        } else {
          this.itemForm.get('stateOfOrigin').clearValidators();
        }
        this.itemForm.get('stateOfOrigin').updateValueAndValidity({ emitEvent: false });

        this.previousCountryOfOriginId = countryId;
        this.cdr.markForCheck();
      });

    const isValidateAfterInit: boolean = this.activeRoute.snapshot.queryParams.validateAfterInit
      ? JSON.parse(this.activeRoute.snapshot.queryParams.validateAfterInit)
      : false;
    if (isValidateAfterInit) {
      this.fh.markAsTouched(this.itemForm);
    }

    if (!this.itemId) {
      this.isInit = true;
    }

    this.initializeCompanyFavoriteHtsNumbers();

    this.entrySummaryStateService.entrySummary$.pipe(takeUntil(this.isDestroy)).subscribe((entrySummary: EntrySummaryModel) => {
      this.entrySummary = entrySummary;
      this.isCanEdit = EntrySummaryActionsHelper.isCanEdit(entrySummary);
      this.isCustomBroker = this.entrySummaryStateService.isShowDocuments(entrySummary);
      this.isShowDocuments = this.isCustomBroker ? true : this.isShowDocuments;
      if (this.entrySummary && this.entrySummary.referenceNumber) {
        this.checkValidation();
      }
      if (entrySummary?.shipments?.length) {
        this.currentShipment = entrySummary.shipments.find((item: ShipmentModel) => item.id === this.shipment?.id);
        this.currentInvoice = this.currentShipment?.invoices.find((item: InvoiceModel) => item.id === this.invoice.id);
        this.currentItem = this.currentInvoice?.lines.find((item: LineModel) => item.id === this.itemId);
        this.arrivalDate = entrySummary.arrivalDate;
        this.updateForm();
        this.syncAddFormEditableState();
        this.updateCurrency();
        this.initializeCountryOfExport();
        this.initializeCountryOfOrigin();
        this.initState();
      }
      // see 5210
      // this.updateTotalESEnteredValue();

      let uploadDocumentsDescription: string,
        uploadDocumentsDescriptionMobile: string = null;
      switch (this.entrySummary?.transportationMode) {
        case enumTransportationMode.AIR:
          uploadDocumentsDescription = 'DOCUMENTS.UPLOAD_DOCUMENTS_DESCRIPTION_AIR';
          uploadDocumentsDescriptionMobile = 'DOCUMENTS.UPLOAD_DOCUMENTS_DESCRIPTION_MOBILE_AIR';
          break;
        case enumTransportationMode.OCEAN:
        case enumTransportationMode.RAIL:
        case enumTransportationMode.TRUCK:
          uploadDocumentsDescription = 'DOCUMENTS.UPLOAD_DOCUMENTS_DESCRIPTION_OCEAN';
          uploadDocumentsDescriptionMobile = 'DOCUMENTS.UPLOAD_DOCUMENTS_DESCRIPTION_MOBILE_OCEAN';
          break;
        default:
          break;
      }

      this.uploadDocumentsDescsription = uploadDocumentsDescription;
      this.uploadDocumentsDescsriptionMobile = uploadDocumentsDescriptionMobile;
    });

    this.entrySummaryStateService.entryDocuments$.pipe(takeUntil(this.isDestroy)).subscribe((documents: DocumentModel[]) => {
      this.attachedDocuments = documents;
      if (this.entrySummary && this.entrySummary.referenceNumber) {
        this.checkValidation();
      }
    });

    this.itemForm.get('countryOfExportId').clearValidators();

    this.favoriteHtsItemsSearchSubject
      .pipe(debounceTime(HTS_SUGGESTIONS_DEBOUNCE_MS), takeUntil(this.isDestroy))
      .subscribe((searchValue) => {
        if (!searchValue) return;

        const searchFilteredHtsList = this.getSearchFilteredHtsList(searchValue);

        return of(searchFilteredHtsList)
          .pipe(
            //Ensures invalid catalog items are filtered out from the results
            map((favoriteHtsItems) => {
              return favoriteHtsItems.filter((fhts) => fhts.isActive);
            }),
            takeUntil(this.isDestroy),
          )
          .subscribe((favoriteHtsItems) => {
            this.favoriteHtsNumberSearchedItems = favoriteHtsItems;
            this.cdr.markForCheck();
          });
      });

    // #13517: the same typed text also asks the backend for similar products (assistant-backed catalog items)
    this.favoriteHtsItemsSearchSubject
      .pipe(
        debounceTime(HTS_SUGGESTIONS_DEBOUNCE_MS),
        switchMap((searchValue: string) =>
          this.isBackendInsert
            ? this.htsLineInsertService.searchByText(searchValue, this.itemId || this.createdLineId || null)
            : of([] as SimilarHtsSuggestionModel[]),
        ),
        takeUntil(this.isDestroy),
      )
      .subscribe((suggestions: SimilarHtsSuggestionModel[]) => {
        this.favoriteHtsSuggestions = suggestions;
        this.cdr.markForCheck();
      });
  }

  public get lineItemTitle(): string {
    if (this.isAddMode) {
      return this.translateService.instant('ENTRY_SUMMARY.NEW_LINE_ITEM');
    }

    const currentLineItemIndex = this.invoice?.lines?.length ? this.invoice.lines.findIndex((line) => line.id === this.itemId) : 0;
    const translatedLineItem = this.translateService.instant('INDEX_LINE_ITEM', { index: this.getNumberedIndex(currentLineItemIndex) });
    return translatedLineItem;
  }

  public get showState(): boolean {
    const id: number = this.itemForm.get('countryOfOriginId')?.value;
    return id === CANADA_COUNTRY_ID || id === MEXICO_COUNTRY_ID || id === USA_COUNTRY_ID;
  }

  public get invoiceNumber(): string {
    return (this.currentInvoice && this.currentInvoice.invoiceNumber) || '';
  }

  public get isRequiredHtsNumber(): boolean {
    return this.entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL;
  }

  public ngOnDestroy(): void {
    this.isDestroyed = true;
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public toggleSpecialHts(): void {
    this.addSpecialChapterHTSCode = !this.addSpecialChapterHTSCode;
    if (!this.addSpecialChapterHTSCode) {
      this.itemForm.get('specialChapterHtsNumber').patchValue(null);
      this.itemForm.get('htsNumber').updateValueAndValidity();
    }
  }

  /**
   * `isAfterAssistant`: the submit resumed by `onAssistantClosed` — the assistant step is behind it. Any other Next while
   * the assistant is on its way or open is ignored: that submit is already pending.
   */
  public onSubmit(needValidate: boolean = true, isAfterAssistant: boolean = false): void {
    if (!this.isCanEdit) {
      return;
    }
    if (needValidate && !isAfterAssistant) {
      if (this.isSubmitPendingAfterAssistant) {
        return;
      }
      // Next on a line the assistant has not verified (#13403): the assistant comes first; the submit resumes when it closes
      if (this.isAssistantStepBeforeSubmit) {
        this.isSubmitPendingAfterAssistant = true;
        this.openHtsAssistant();
        return;
      }
    }
    this.isSubmitPendingAfterAssistant = false;
    if (needValidate) {
      const invalidFields: any = this.itemForm.invalid ? this.fh.validateAllFields(this.itemForm) : {};

      if (invalidFields.number && this.itemForm.get('specialChapterHts').get('number').invalid) {
        delete invalidFields.number;
        invalidFields.specialChapterHtsCode = 'specialChapterHtsCode';
      }
      // the assistant has not verified the line: listed with the missing fields (the user may still continue as is)
      if (this.isHtsValidationNeeded) {
        invalidFields.htsValidation = 'htsValidation';
      }

      if (Object.keys(invalidFields).length) {
        this.openModal(invalidFields);
        this.cdr.markForCheck();
        return;
      }
    }

    this.updateOrCreateLineItem(this.buildLinePayload(), this.invoice.id)
      .pipe(
        takeUntil(this.isDestroy),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
      )
      .subscribe((entry) => {
        // #12298: edits are persisted — clear dirty so future server refreshes apply again.
        this.itemForm.markAsPristine();

        if (this.isAddMode) {
          // #12863 — stay on the step with a blank form so the user can keep adding lines.
          // Reset BEFORE pushing the entry: entrySummary$ re-enters this component synchronously and would
          // otherwise seed country/state lookups from the just-saved values and overwrite the cleared init lists.
          this.resetAddForm();
          this.entrySummaryStateService.updateCurrentEntrySummary(entry);
          this.alertService.success('ENTRY_SUMMARY.LINE_ITEM_ADDED');
          this.cdr.markForCheck();
          return;
        }

        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.dataSaved.emit();
      });
  }

  public searchUnits(): Observable<UnitModel[]> {
    return this.codeRepository.searchLineUnits.bind(this.codeRepository);
  }

  public onFavoriteHtsItemsSearch() {
    //#11822 - skip search if no catalog items are saved for company
    if (!this.companyFavoriteHtsNumbers.length) {
      return;
    }

    const favoriteHtsItemsSearchText = this.itemForm.get('description').value;
    this.favoriteHtsItemsSearchSubject.next(favoriteHtsItemsSearchText);
  }

  /** The legacy pick (untagged companies): the item's title (else description), cut to the field limit, and the code go into the form. */
  public onHtsInserted(favoriteHtsNumber: FavoriteHtsNumberModel): void {
    const description: string = favoriteHtsNumber.title || favoriteHtsNumber.description || '';
    this.setHTS({
      hts: favoriteHtsNumber.htsNumber,
      description: UtilsHelper.limitLength(description, LINE_ITEM_DESCRIPTION_MAX_LENGTH, false),
    });
    this.clearSearchedFavoriteHtsItems();
    this.cdr.markForCheck();
  }

  public clearSearchedFavoriteHtsItems() {
    this.favoriteHtsNumberSearchedItems = [];
    this.favoriteHtsSuggestions = [];
    // an empty text supersedes a pending debounce and the similar-products request in flight: nothing reopens the dropdown
    this.favoriteHtsItemsSearchSubject.next('');
  }

  public get isShowFindInCatalog(): boolean {
    return this.companyFavoriteHtsNumbers.length > 0;
  }

  /** The fast-search dropdown: exact catalog matches first, then the similar products (#13517). */
  public get isShowFastSearch(): boolean {
    return this.favoriteHtsNumberSearchedItems.length > 0 || this.favoriteHtsSuggestions.length > 0;
  }

  /** Tagged companies editing their own entry insert catalog codes through the backend (code only); everyone else fills the form. */
  public get isBackendInsert(): boolean {
    const scope: enumHtsChatScope = this.htsChatScope || enumHtsChatScope.Importer;
    return this.htsLineInsertService.isEnabled && this.isCanEdit && scope === enumHtsChatScope.Importer;
  }

  /**
   * A catalog pick from the dropdown or the sidebar (#13411). Tagged companies: the line is saved first and the backend
   * writes the code — the description stays the user's own (an empty one takes the item's title so the line can be saved).
   */
  public onCatalogPick(favoriteHtsNumber: FavoriteHtsNumberModel): void {
    if (!this.isBackendInsert) {
      this.onHtsInserted(favoriteHtsNumber);
      return;
    }
    this.clearSearchedFavoriteHtsItems();
    this.fillEmptyDescription(favoriteHtsNumber.title || favoriteHtsNumber.description);
    this.insertThroughBackend((lineId: number) => this.htsLineInsertService.insertCatalogItem(lineId, favoriteHtsNumber));
  }

  /** A similar-product suggestion (#13517): confirmed through the backend, the line comes back "matched by similarity". */
  public onSuggestionPick(suggestion: SimilarHtsSuggestionModel): void {
    this.clearSearchedFavoriteHtsItems();
    if (!this.isBackendInsert) {
      return;
    }
    this.fillEmptyDescription(suggestion.title || suggestion.matchedText);
    this.insertThroughBackend((lineId: number) => this.htsLineInsertService.confirmSuggestion(lineId, suggestion));
  }

  public getHtsUnits(data: HtsNumberModel[]): void {
    if (data && (isNaN(this.currentItem?.quantity1) || this.currentItem?.quantity1 === null)) {
      const prevValues: any[] = this.itemForm.get('units').value;
      const items: UntypedFormArray = this.formUnits;
      const hts: HtsNumberModel = data.length === 1 ? data[0] : null;
      this.clearUnits();
      if (hts) {
        if (hts.unit1) {
          items.push(this.createUnit(1, hts.unit1, this.getQuantity(prevValues, 1)));
        }
        if (hts.unit2) {
          items.push(this.createUnit(2, hts.unit2, this.getQuantity(prevValues, 2)));
        }
        if (hts.unit3) {
          items.push(this.createUnit(3, hts.unit3, this.getQuantity(prevValues, 3)));
        }
        this.unitsToMachForSpecialHts = [hts.unit1, hts.unit2, hts.unit3].filter((unit: string) => !!unit);
      }
      this.cdr.markForCheck();
    }
  }

  public removeUnit(index: number): void {
    const items: UntypedFormArray = this.formUnits;
    const units: Array<{ quantity: number; unitOfMeasure: string }> = items.value
      .filter((unit: any) => !unit.hasOwnProperty('quantity' + (index + 1)))
      .map((unit: any) => ({
        quantity: unit.quantity1 || unit.quantity2 || unit.quantity3 || null,
        unitOfMeasure: unit.unitOfMeasure1 || unit.unitOfMeasure2 || unit.unitOfMeasure3 || null,
      }));
    this.clearUnits();

    units.forEach((item: { quantity: number; unitOfMeasure: string }, unitIndex: number) => {
      items.push(this.createUnit(unitIndex + 1, item.unitOfMeasure, item.quantity));
    });
  }

  public back(): void {
    this.location.back();
  }

  public get formUnits(): UntypedFormArray {
    return <UntypedFormArray>this.itemForm.get('units');
  }

  public onDocumentStateUpdate(data: IEsDocumentStateUpdate): void {
    this.entrySummaryStateService.updateDocumentState(data.documentId, data.state);
  }

  public getCountries(): Observable<CountryModel[]> {
    return this.getCountriesCallApi.bind(this);
  }

  public getCountriesCallApi(code: string): Observable<CountryModel[]> {
    const call: Observable<CountryModel[]> = this.codeRepository.searchCountry(code);
    call.pipe(take(1)).subscribe((data: CountryModel[]) => (this.lastSearchedCountries = data));

    return call;
  }

  public getStates(): Observable<StateModel[]> {
    return this.codeRepository.searchState.bind(this.codeRepository, this.itemForm.get('countryOfOrigin')?.value?.code);
  }

  public clearForm(): void {
    this.itemForm.reset({
      id: this.itemForm.get('id')?.value,
      hasNaftaNetCostIndicator: this.itemForm.get('hasNaftaNetCostIndicator')?.value,
    });
  }

  public findInCatalog() {
    this.appService.isXlg$.next(true);

    const componentSidebarRef: ComponentRef<BinaCatalogSearchSidebarComponent> = this.appService.openSidebarAsWindow(
      BinaCatalogSearchSidebarComponent,
      {
        sidebarHeaderText: '',
        isMd: true,
        customZIndex: '1032',
      },
    );

    componentSidebarRef.instance.company = this.companiesService.getCompany();
    componentSidebarRef.instance.initialSearch = this.isBackendInsert ? this.itemForm.get('description').value : null;

    // the sidebar pick follows the dropdown pick: title as the description (#13545), backend insert for tagged companies
    componentSidebarRef.instance.htsInsert.pipe(takeUntil(this.isDestroy)).subscribe((favoriteHts: FavoriteHtsNumberModel) => {
      this.closeCatalogSidebar();
      this.onCatalogPick(favoriteHts);
    });

    componentSidebarRef.instance.suggestionInsert.pipe(takeUntil(this.isDestroy)).subscribe((suggestion: SimilarHtsSuggestionModel) => {
      this.closeCatalogSidebar();
      this.onSuggestionPick(suggestion);
    });

    componentSidebarRef.instance.close.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    });
  }

  public get isHtsAssistantEnabled(): boolean {
    return this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant);
  }

  /** The saved line still needs the classification assistant (#13403): the button says "Validate HTS" and stands out. */
  public get isHtsValidationNeeded(): boolean {
    const scope: enumHtsChatScope = this.htsChatScope || enumHtsChatScope.Importer;
    return (
      this.isHtsAssistantEnabled &&
      this.isCanEdit &&
      scope === enumHtsChatScope.Importer &&
      !!this.currentItem &&
      HtsLineStatusHelper.isNeedsClassification(this.currentItem)
    );
  }

  public get findHtsLabelKey(): string {
    return this.isHtsValidationNeeded ? 'VALIDATE_HTS' : 'FIND_HTS';
  }

  public findHTS(): void {
    if (this.isHtsAssistantEnabled) {
      // F15: the assistant classifies the SAVED description — flag the field instead of saving / opening without one
      if (HtsAssistantDescriptionGuard.require(this.itemForm.get('description'), this.elementRef.nativeElement)) {
        this.openHtsAssistant();
      }
      this.cdr.markForCheck();
      return;
    }

    this.appService.isXlg$.next(true);

    const componentSidebarRef: ComponentRef<BinaSidebarComponent> = this.appService.openSidebarAsWindow(BinaSidebarComponent, {
      sidebarHeaderText: '',
      isMd: true,
      customZIndex: '1032',
    });

    componentSidebarRef.instance.importerId = this.entrySummary?.importerId;
    componentSidebarRef.instance.manufacturerId = this.currentInvoice?.manufacturerId;
    componentSidebarRef.instance.existingDescription = this.itemForm.get('description').value;
    componentSidebarRef.instance.existingHTS = this.itemForm.get('htsNumber').value;
    componentSidebarRef.instance.htsFound.pipe(takeUntil(this.isDestroy)).subscribe((data: { hts: string; description: string }) => {
      this.setHTS(data);
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    });
    componentSidebarRef.instance.close.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    });
  }

  /** Only an EMPTY description is filled from the catalog, so a line can be saved; the user's own text is never replaced. */
  private fillEmptyDescription(text: string | null | undefined): void {
    const current: string = this.itemForm.get('description').value;
    if (HtsAssistantHelper.normalizeDescription(current) || !text) {
      return;
    }
    this.itemForm.get('description').setValue(UtilsHelper.limitLength(text, LINE_ITEM_DESCRIPTION_MAX_LENGTH, false));
    this.itemForm.get('description').markAsDirty();
    this.itemForm.markAsDirty();
  }

  /** Save the draft (POST / PUT / nothing), let the backend write the code, mirror only the code in the form. */
  private insertThroughBackend(request: (lineId: number) => Observable<LineModel>): void {
    if (this.isPreSavingForAssistant) {
      return;
    }
    this.isPreSavingForAssistant = true;
    this.saveLineForAssistant()
      .pipe(
        switchMap(request),
        take(1),
        takeUntil(this.isDestroy),
        finalize(() => {
          this.isPreSavingForAssistant = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(
        (line: LineModel) => {
          this.itemForm.get('htsNumber').setValue(line.htsNumber);
          this.lineInserted.emit(line);
        },
        // the service already raised the alert; the draft stays as it is
        () => this.lineInsertFailed.emit(),
      );
  }

  private closeCatalogSidebar(): void {
    this.appService.closeSidebarAsWindow();
    this.appService.isXlg$.next(false);
  }

  private initializeCompanyFavoriteHtsNumbers() {
    this.binaRepository
      .searchFavoriteHtsNumbers({ companyId: this.company?.id })
      .pipe(takeUntil(this.isDestroy))
      .subscribe((result) => {
        const { items } = result;
        this.companyFavoriteHtsNumbers = items;
        this.cdr.detectChanges();
      });
  }

  private getSearchFilteredHtsList(searchValue: string): FavoriteHtsNumberModel[] {
    return this.companyFavoriteHtsNumbers.filter((fhts) => {
      const lowerCaseSearchValue = searchValue.toLocaleLowerCase();

      const isMatchWithHtsNumber = fhts?.htsNumber.toLowerCase().includes(lowerCaseSearchValue);
      const isMatchWithSearchQuery = fhts?.searchQuery.toLowerCase().includes(lowerCaseSearchValue);
      const isMatchWithTitle = fhts?.title.toLowerCase().includes(lowerCaseSearchValue);
      const isMatchWithDescription = fhts?.description.toLowerCase().includes(lowerCaseSearchValue);

      return isMatchWithHtsNumber || isMatchWithSearchQuery || isMatchWithTitle || isMatchWithDescription;
    });
  }

  private initializeCountryOfOrigin(): void {
    if (!this.itemForm.get('countryOfOriginId').value) {
      return;
    }

    if (this.itemForm.get('countryOfOrigin')?.value?.code) {
      this.codeRepository
        .getCountry(this.itemForm.get('countryOfOrigin')?.value?.code)
        .pipe(takeUntil(this.isDestroy))
        .subscribe((country: CountryModel) => {
          this.initCountryOfOrigin = [country];
          this.cdr.markForCheck();
        });
    }
  }

  private initializeCountryOfExport(): void {
    if (!this.itemForm.get('countryOfExportId').value) {
      return;
    }

    if (this.itemForm.get('countryOfExport')?.value?.code) {
      this.codeRepository
        .getCountry(this.itemForm.get('countryOfExport')?.value?.code)
        .pipe(takeUntil(this.isDestroy))
        .subscribe((country: CountryModel) => {
          this.initCountryOfExport = [country];
          this.cdr.markForCheck();
        });
    }
  }

  private initState(): void {
    if (!this.itemForm.get('stateOfOrigin').value) {
      return;
    }
    this.codeRepository
      .getState(this.itemForm.get('stateOfOrigin').value)
      .pipe(takeUntil(this.isDestroy))
      .subscribe((state: StateModel) => {
        this.initStateOfOrigin = [state];
      });
  }

  private checkValidation(): void {
    this.entrySummaryStateService
      .getESValidationErrorData(this.entrySummary, this.attachedDocuments)
      .pipe(takeUntil(this.isDestroy))
      .subscribe((data: IEsValidationData) => {
        this.validationMessages = data.messages;
      });
  }

  private getQuantity(prevValues: any[], index: number): number {
    return prevValues && prevValues.length > index - 1 && prevValues[index - 1]['quantity' + index]
      ? prevValues[index - 1]['quantity' + index]
      : null;
  }

  private createUnit(index: number, unit: string, quantity: string | number = ''): UntypedFormGroup {
    const form: UntypedFormGroup = new UntypedFormGroup({
      ['quantity' + index]: new UntypedFormControl(quantity, this.isRequiredHtsNumber ? Validators.required : []),
      ['unitOfMeasure' + index]: new UntypedFormControl(unit, this.isRequiredHtsNumber ? Validators.required : []),
    });

    if (this.itemForm.disabled) {
      form.disable();
    } else {
      form.enable();
    }
    return form;
  }

  private openModal(data): void {
    this.missingFieldModal.openCustomModal(data);
  }

  private updateForm(): void {
    if (!this.currentItem) {
      return;
    }
    // #12298: don't re-patch the form from a background reload while the user is mid-edit — it would clobber unsaved input.
    if (this.itemForm.dirty) {
      return;
    }
    const dataToUpdate = {
      id: this.itemId || null,
      countryOfOrigin: this.currentItem.countryOfOrigin,
      countryOfOriginId: this.currentItem.countryOfOrigin?.id,
      htsNumber: this.currentItem.htsNumber,
      stateOfOrigin: this.currentItem.stateOfOrigin,
      description: this.currentItem.description,
      grossWeight: !!this.currentItem.grossWeight ? this.currentItem.grossWeight : '',
      value: !!this.currentItem.value ? this.currentItem.value : '',
      specialChapterHtsNumber: this.currentItem.specialChapterHtsNumber,
      hasNaftaNetCostIndicator: this.currentItem.hasNaftaNetCostIndicator,
      countryOfExport: this.invoice?.countryOfExport,
      countryOfExportId: this.invoice?.countryOfExportId,
    };

    this.itemForm.patchValue(dataToUpdate, { emitEvent: false });

    const units: UntypedFormArray = this.formUnits;
    this.clearUnits();
    if (this.currentItem.quantity1 || this.currentItem.unitOfMeasure1) {
      units.push(this.createUnit(1, this.currentItem.unitOfMeasure1, !!this.currentItem.quantity1 ? this.currentItem.quantity1 : ''));
    }
    if (this.currentItem.quantity2 || this.currentItem.unitOfMeasure2) {
      units.push(this.createUnit(2, this.currentItem.unitOfMeasure2, !!this.currentItem.quantity2 ? this.currentItem.quantity2 : ''));
    }
    if (this.currentItem.quantity3 || this.currentItem.unitOfMeasure3) {
      units.push(this.createUnit(3, this.currentItem.unitOfMeasure3, !!this.currentItem.quantity3 ? this.currentItem.quantity3 : ''));
    }
    if (this.currentItem.specialChapterHtsNumber) {
      this.addSpecialChapterHTSCode = true;
    }
    if (this.isCanEdit) {
      this.itemForm.enable();
    } else {
      this.itemForm.disable();
    }

    this.isInit = true;
  }

  private clearUnits(): void {
    const units: UntypedFormArray = this.formUnits;
    while (units.length !== 0) {
      units.removeAt(0);
    }
  }

  /** #12863 — add mode has no `currentItem`, so `updateForm` never runs its enable/disable branch; mirror it here. */
  private syncAddFormEditableState(): void {
    if (!this.isAddMode) {
      return;
    }

    if (this.isCanEdit) {
      this.itemForm.enable();
    } else {
      this.itemForm.disable();
    }
  }

  /** #12863 — back to the pristine blank state of a freshly created form (add mode only). */
  private resetAddForm(): void {
    this.clearUnits();
    this.itemForm.reset({ hasNaftaNetCostIndicator: false }, { emitEvent: false });
    // the country listener is skipped by emitEvent:false — drop the state requirement it may have set for the previous country
    this.itemForm.get('stateOfOrigin').clearValidators();
    this.itemForm.get('stateOfOrigin').updateValueAndValidity({ emitEvent: false });
    this.addSpecialChapterHTSCode = false;
    this.createdLineId = null;
    this.previousCountryOfOriginId = null;
    this.unitsToMachForSpecialHts = [];
    this.initCountryOfOrigin = [];
    this.initStateOfOrigin = [];
    this.favoriteHtsNumberSearchedItems = [];
  }

  private updateCurrency(): void {
    if (!this.currentShipment || !this.currentInvoice) {
      this.invoiceCurrencyCode = DEFAULT_CURRENCY_CODE;
      return;
    }
    this.invoiceCurrencyCode = this.currentInvoice.currency || DEFAULT_CURRENCY_CODE;
    const exportDate: string = this.currentShipment.exportDate || DateHelper.now;
    if (this.invoiceCurrencyCode !== DEFAULT_CURRENCY_CODE && this.invoiceCurrencyCode) {
      this.currenciesRepository
        .getRate(this.invoiceCurrencyCode, exportDate)
        .pipe(takeUntil(this.isDestroy))
        .subscribe((currencyRate: CurrencyRateModel) => {
          this.invoiceCurrencyRate = currencyRate ? currencyRate.rate : NaN;
          // TODO: check setCurrencyRate using, not direct logic
          this.cdr.detectChanges();
          // see 5210
          // this.updateTotalESEnteredValue();
        });
    } else {
      this.invoiceCurrencyRate = 1;
    }
  }

  private createShouldNotMatchHTSConfig(): any {
    return {
      htsNumber: {
        relatedControlName: 'specialChapterHtsNumber',
        errorMessage: 'shouldNotMatchSpecialChapterHts',
        relatedControlErrorMessage: 'shouldNotMatchHts',
      },
      specialChapterHtsNumber: {
        relatedControlName: 'htsNumber',
        errorMessage: 'shouldNotMatchHts',
        relatedControlErrorMessage: 'shouldNotMatchSpecialChapterHts',
      },
    };
  }

  /** What Add / Next send; in add mode a line persisted by "Find HTS" keeps its id so Add updates instead of duplicating. */
  private buildLinePayload(): LineModel {
    const { htsNumber, countryOfOrigin, units, specialChapterHtsNumber } = this.itemForm.value;
    const dataUnits = units.reduce((store, item) => {
      const keys = Object.keys(item);
      const updatedItem = {};
      keys.forEach((dataItem: string) => {
        if (!dataItem.includes('unitOfMeasure')) {
          updatedItem[dataItem] = !isNaN(Number(item[dataItem])) ? Number(item[dataItem]) : item[dataItem];
        } else {
          updatedItem[dataItem] = item[dataItem];
        }
      });
      store = { ...store, ...updatedItem };
      return store;
    }, {});
    return {
      ...this.itemForm.value,
      ...dataUnits,
      htsNumber,
      countryOfOrigin,
      specialChapterHtsNumber: this.addSpecialChapterHTSCode ? specialChapterHtsNumber : null,
      id: this.itemId || this.createdLineId || null,
      grossWeight: Number(this.itemForm.value.grossWeight),
      value: Number(this.itemForm.value.value),
    };
  }

  /** #13383 F6: persist the draft before the assistant opens (POST new / PUT changed / nothing when untouched). */
  private saveLineForAssistant(): Observable<number> {
    const knownId: number | null = this.itemId || this.createdLineId || null;
    if (knownId && !this.itemForm.dirty) {
      return of(knownId);
    }
    return this.updateOrCreateLineItem(this.buildLinePayload(), this.invoice.id).pipe(
      tap((line: LineModel) => {
        if (!knownId) {
          this.createdLineId = line.id;
        }
      }),
      switchMap((line: LineModel) =>
        this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber).pipe(
          tap((entry: EntrySummaryModel) => this.entrySummaryStateService.updateCurrentEntrySummary(entry)),
          map(() => line.id),
          // the line IS saved: a failed refresh must not hide the drawer or leave the id behind (next reload heals the lists)
          catchError(() => of(line.id)),
        ),
      ),
      tap(() => {
        this.itemForm.markAsPristine();
        this.cdr.markForCheck();
      }),
    );
  }

  /** #13383: the classification assistant drawer replaces the BINA classifier for tagged companies. */
  private openHtsAssistant(): void {
    if (this.isPreSavingForAssistant) {
      return;
    }
    const scope: enumHtsChatScope = this.htsChatScope || enumHtsChatScope.Importer;
    const mode: enumHtsAssistantMode =
      this.isCanEdit && scope === enumHtsChatScope.Importer ? enumHtsAssistantMode.Live : enumHtsAssistantMode.Readonly;
    const lineId$: Observable<number | null> =
      mode === enumHtsAssistantMode.Live ? this.saveLineForAssistant() : of(this.itemId || this.createdLineId || null);
    this.isPreSavingForAssistant = true;
    lineId$.pipe(take(1), takeUntil(this.isDestroy)).subscribe(
      (lineId: number | null) => {
        this.isPreSavingForAssistant = false;
        this.launchHtsAssistant(mode, scope, lineId);
      },
      () => {
        // the global alert already told the user; the drawer stays closed and a pending Next is forgotten
        this.isPreSavingForAssistant = false;
        this.isSubmitPendingAfterAssistant = false;
      },
    );
  }

  /** Without a description there is nothing to classify: the regular validation lists the description and the HTS row. */
  private get isAssistantStepBeforeSubmit(): boolean {
    return this.isHtsValidationNeeded && !!HtsAssistantHelper.normalizeDescription(this.itemForm.get('description').value);
  }

  /**
   * The drawer is gone (a microtask later: the ref is still being torn down). The assistant and its line panel write to
   * the store, so the form follows it — but only if the store line really changed: after a failed entry re-read the form
   * (with the code the assistant put there) is the better copy. Then a pending Next resumes.
   */
  private onAssistantClosed(): void {
    Promise.resolve().then(() => {
      if (this.isDestroyed) {
        return;
      }
      if (this.isStoreLineChangedSinceDrawerOpen) {
        this.itemForm.markAsPristine();
        this.updateForm();
      }
      if (this.isSubmitPendingAfterAssistant) {
        this.onSubmit(true, true);
      }
      this.cdr.markForCheck();
    });
  }

  private get isStoreLineChangedSinceDrawerOpen(): boolean {
    return !!this.currentItem && this.currentItem.lastModified !== this.lineModifiedAtDrawerOpen;
  }

  private launchHtsAssistant(mode: enumHtsAssistantMode, scope: enumHtsChatScope, lineId: number | null): void {
    this.lineModifiedAtDrawerOpen = this.currentItem ? this.currentItem.lastModified : null;
    const drawerRef: ComponentRef<HtsAssistantDrawerComponent> = this.htsAssistantLauncherService.open({
      mode,
      scope,
      lineId,
      invoiceId: this.invoiceId || null,
      companyId: this.company ? this.company.id : null,
      lineDescription: this.itemForm.get('description').value,
      currentHts: this.itemForm.get('htsNumber').value,
      customZIndex: HTS_LINE_PANEL_Z_INDEX,
      hostDestroy$: this.isDestroy,
      draftDescription$: this.itemForm.get('description').valueChanges,
      // the panel edits the stored line; an add-mode form has no stored line to follow (its edits would be PUT over)
      isLinePanel: !!this.itemId,
    });

    drawerRef.instance.htsFound.pipe(takeUntil(this.isDestroy)).subscribe((data: IHtsAssistantHtsFound) => {
      // only the code comes from the assistant: the description stays the user's own (#13384 F15)
      this.setHTS({ hts: data.hts, description: data.description || this.itemForm.get('description').value });
      this.cdr.markForCheck();
    });
    // closed by the user, after an Insert (#13529) or with the host: the form follows the store, a pending Next resumes
    drawerRef.onDestroy(() => this.onAssistantClosed());
  }

  private setHTS(data: { hts: string; description: string }): void {
    if (!data) {
      return;
    }
    this.itemForm.get('htsNumber').setValue(data.hts);
    this.itemForm.get('description').setValue(data.description);
  }

  private updateOrCreateLineItem(data: LineModel, invoiceId?: number): Observable<LineModel> {
    if (data.id) {
      return this.updateLineItem(data);
    } else {
      return this.createNewLineItem(data, invoiceId);
    }
  }

  private updateLineItem(data): Observable<LineModel> {
    return this.entrySummaryStateService.updateLineItem(data, data.id);
  }

  private createNewLineItem(data, invoiceId: number): Observable<LineModel> {
    return this.entrySummaryStateService.createLineItem(data, invoiceId);
  }

  private getNumberedIndex(index): string | null {
    return !!index || index === 0 ? (index + 1).toString() : null;
  }
}
